import React from 'react';
import { Building2, Users, Shield, UserCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-6xl mx-auto text-center space-y-6">
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            About Coinexus
          </h1>
        </div>
      </section>

      {/* About Coinexus Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-gray-200 shadow-lg rounded-xl bg-white">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Building2 className="h-8 w-8 text-[#FF6B35]" />
                <h2 className="text-3xl font-bold text-[#1E3A5F]">About Coinexus</h2>
              </div>
              <div className="space-y-4 text-[#5B6B7F] text-base md:text-lg leading-relaxed">
                <p>
                  Coinexus Sp. z o.o. is a Poland-registered Virtual Asset Service Provider (VASP), providing compliant OTC crypto-to-fiat and fiat-to-crypto exchange services for institutional and qualified clients.
                </p>
                <p>
                  We operate strictly under EU 5AMLD/6AMLD frameworks and the Polish Anti-Money Laundering and Counter-Terrorist Financing Act.
                </p>
                <p>
                  Our mission is to ensure secure, transparent, and regulation-driven digital-asset transactions supported by strong compliance controls, risk-management processes, and industry-leading standards.
                </p>
                <p>
                  Coinexus works exclusively with verified clients and applies strict onboarding, AML/KYC procedures, and continuous transaction-monitoring protocols.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Management & Compliance Team Section */}
      <section className="py-12 px-6 bg-[#FAFBFC]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Users className="h-10 w-10 text-[#FF6B35]" />
              <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
                Management & Compliance Team
              </h2>
            </div>
          </div>

          <div className="space-y-6">
            {/* Team Member 1 */}
            <Card className="border border-gray-200 shadow-md rounded-xl bg-white hover:shadow-lg transition-shadow">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-[#1E3A5F] mb-2">
                  Mohamed Oussema Sassi — Co-Founder, Executive Director & MLRO
                </h3>
                <div className="space-y-3 text-[#5B6B7F] leading-relaxed">
                  <p>
                    Mohamed Oussema Sassi is the Co-Founder and Executive Director of Coinexus Sp. z o.o., and also serves as the Company's officially appointed Money Laundering Reporting Officer (MLRO), effective 29 September 2025.
                  </p>
                  <p>
                    Mohamed brings extensive experience in OTC digital-asset markets, liquidity evaluation, AML/CTF compliance, sanctions/PEP screening oversight, transaction-risk assessment, and regulatory reporting to GIIF.
                  </p>
                  <p>
                    He is responsible for:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>AML/CTF governance and compliance management</li>
                    <li>Client identification and verification oversight</li>
                    <li>Risk-based monitoring of transaction activity</li>
                    <li>Sanctions & PEP screening supervision</li>
                    <li>Suspicious-activity reporting obligations</li>
                    <li>Development and implementation of internal AML/KYC policies</li>
                    <li>Ensuring full compliance with EU and Polish AML legislation</li>
                  </ul>
                  <p className="mt-3">
                    Mohamed leads the company's strategic direction, operational compliance framework, and overall regulatory alignment.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Team Member 2 */}
            <Card className="border border-gray-200 shadow-md rounded-xl bg-white hover:shadow-lg transition-shadow">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-[#1E3A5F] mb-2">
                  Houssem Bouaffoura — Authorized Commercial Representative
                </h3>
                <div className="space-y-3 text-[#5B6B7F] leading-relaxed">
                  <p>
                    Houssem Bouaffoura serves as the Authorized Commercial Representative of Coinexus Sp. z o.o.
                  </p>
                  <p>
                    He supports commercial operations, institutional client relations, and communication with partners, ensuring that all client-facing processes align with Coinexus' regulated and compliance-driven operational model.
                  </p>
                  <p>
                    His functions include:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Commercial coordination and client communications</li>
                    <li>Support in operational execution</li>
                    <li>Ensuring market activities align with internal compliance standards</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Regulatory & Technology Partners Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-gray-200 shadow-lg rounded-xl bg-white">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="h-8 w-8 text-[#FF6B35]" />
                <h2 className="text-3xl font-bold text-[#1E3A5F]">Regulatory & Technology Partners</h2>
              </div>
              <div className="space-y-4 text-[#5B6B7F] text-base md:text-lg leading-relaxed">
                <p>
                  We operate exclusively with regulated compliance and liquidity partners to maintain secure and transparent operations:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><span className="font-semibold text-[#1E3A5F]">KYC/KYT Provider:</span> Allpass.ai</li>
                  <li><span className="font-semibold text-[#1E3A5F]">Regulated liquidity partners</span> (details shared with counterparties upon request)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Client Acquisition & How Clients Find Us Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="border border-gray-200 shadow-lg rounded-xl bg-white">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <UserCheck className="h-8 w-8 text-[#FF6B35]" />
                <h2 className="text-3xl font-bold text-[#1E3A5F]">Client Acquisition & How Clients Find Us</h2>
              </div>
              <div className="space-y-4 text-[#5B6B7F] text-base md:text-lg leading-relaxed">
                <p>
                  Coinexus strictly operates through professional and compliance-approved channels.
                </p>
                <p>
                  Our clients typically reach us via:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Verified business introductions and institutional referrals</li>
                  <li>Existing partners and regulated financial entities we cooperate with</li>
                  <li>Direct onboarding inquiries submitted through our official website</li>
                </ul>
                <p>
                  Coinexus does not engage in public advertising, mass-marketing, or promotional campaigns.
                </p>
                <p>
                  All clients are accepted only after completing full KYC/AML verification as required under Polish VASP regulations.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;